import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getEstadoColor, getSubEstadoColor } from '../../constants/reservaColors';
import GeolocationCheck from '../../components/GeolocationCheck';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
} from '@chakra-ui/react';

const BolsaServicios = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [reservasOriginal, setReservasOriginal] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [precioAdjudicacion, setPrecioAdjudicacion] = useState(null);
  const [botonesDeshabilitados, setBotonesDeshabilitados] = useState(false);
  const [isTestEnvironment, setIsTestEnvironment] = useState(false);
  const [geolocationGranted, setGeolocationGranted] = useState(false);
  const [creditoConductor, setCreditoConductor] = useState(0);
  const [isModalCreditoOpen, setIsModalCreditoOpen] = useState(false);
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);
  
  const columnsToDisplay = [
    'me_interesa',
    'estado',
    'sub_estado',
    'idioma',
    'tipo',
    'fecha_hora_inicio',
    'fecha_hora_fin',
    'origen_codigo_postal',
    'destino_codigo_postal',
    'precio',
    'distancia_km',
    'created_at',
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Verificar si venimos de un error de pago
        const estado = searchParams.get('estado');
        const tipo = searchParams.get('tipo');
        
        if (estado === 'ko' && tipo === 'adjudicacion') {
          toast({
            title: 'Error en el pago',
            description: 'No se pudo procesar el pago. Por favor, inténtalo de nuevo.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          // Limpiar parámetros de la URL
          navigate('/conductor/bolsa-servicios', { replace: true });
        }
        
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const token = localStorage.getItem('auth-token');
        
        // Si no hay user_id, no podemos continuar (el contexto aún está cargando)
        if (!user || !user.user_id) {
          return;
        }
        
        // Detectar si estamos en entorno de test (localhost)
        const isTest = baseURL && (baseURL.includes('localhost') || baseURL.includes('127.0.0.1'));
        setIsTestEnvironment(isTest);
        
        // Obtener crédito del conductor
        try {
          const conductorResponse = await fetch(`${baseURL}/api/conductores/${user.user_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (conductorResponse.ok) {
            const conductorData = await conductorResponse.json();
            setCreditoConductor(conductorData.credito || 0);
          }
        } catch (creditoError) {
          console.error('Error al obtener crédito del conductor:', creditoError);
          setCreditoConductor(0);
        }
        
        // Obtener precio máximo de adjudicación desde la configuración del sistema
        try {
          console.log('[BolsaServicios] Obteniendo precio máximo de adjudicación...');
          console.log('[BolsaServicios] URL:', `${baseURL}/api/settings/public/precio_maximo_adjudicacion`);
          
          const precioResponse = await fetch(`${baseURL}/api/settings/public/precio_maximo_adjudicacion`, {
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          console.log('[BolsaServicios] Response status:', precioResponse.status);
          console.log('[BolsaServicios] Response ok:', precioResponse.ok);
          
          if (precioResponse.ok) {
            const precioData = await precioResponse.json();
            console.log('[BolsaServicios] Precio obtenido:', precioData);
            console.log('[BolsaServicios] Tipo de dato:', typeof precioData);
            setPrecioAdjudicacion(precioData || 1.0);
            console.log('[BolsaServicios] Precio establecido:', precioData || 1.0);
          } else {
            // Si falla, usar valor por defecto
            const errorText = await precioResponse.text();
            console.error('[BolsaServicios] Error en la respuesta:', errorText);
            console.log('[BolsaServicios] Usando fallback: 1.0');
            setPrecioAdjudicacion(1.0);
          }
        } catch (precioError) {
          console.error('[BolsaServicios] Error al obtener precio de adjudicación:', precioError);
          console.log('[BolsaServicios] Usando fallback: 1.0');
          setPrecioAdjudicacion(1.0);
        }
        
        const response = await fetch(`${baseURL}/api/reservas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            `Error fetching reservas: ${response.status} - ${
              errorData?.detail || response.statusText
            }`
          );
        }
        const data = await response.json();
        
        // Filtrar solo reservas en estado activo y sub-estado bolsa
        const reservasBolsa = data.filter(reserva => 
          reserva.estado === 'activo' && reserva.sub_estado === 'bolsa'
        );
        
        setReservasOriginal(reservasBolsa);
        setReservas(reservasBolsa);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [user, searchParams, toast, navigate]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    if (sortBy) {
      const sortedReservas = [...reservasOriginal].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortOrder === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        } else {
          const stringA = String(valueA);
          const stringB = String(valueB);
          return sortOrder === 'asc'
            ? stringA.localeCompare(stringB)
            : stringB.localeCompare(stringA);
        }
      });
      setReservas(sortedReservas);
    } else {
      setReservas(reservasOriginal);
    }
  }, [sortBy, sortOrder, reservasOriginal]);

  const handleAdjudicarServicio = async (reservaId, metodo = 'T') => {
    try {
      setBotonesDeshabilitados(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/pagos/adjudicar-servicio/${reservaId}?metodo=${metodo}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al iniciar pago');
      }
      
      const data = await response.json();
      
      // 🔥 Si estamos en modo test, redirigir directamente sin pasar por Redsys
      if (data.test_mode && data.redirect_url) {
        console.log('[TEST MODE] Redirección directa:', data.redirect_url);
        toast({
          title: 'Servicio adjudicado',
          description: data.message || 'Pago simulado en entorno de pruebas',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Redirigir después de un breve delay para que se vea el toast
        setTimeout(() => {
          window.location.href = data.redirect_url;
        }, 500);
        return;
      }
      
      // Modo producción: crear formulario oculto para enviar POST a pasarela de pago
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.url_pasarela;
      
      Object.entries(data.form_params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      
      document.body.appendChild(form);
      form.submit();
      
    } catch (error) {
      setBotonesDeshabilitados(false);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAdjudicarConCredito = (reserva) => {
    setReservaSeleccionada(reserva);
    setIsModalCreditoOpen(true);
  };

  const confirmarAdjudicacionCredito = async () => {
    try {
      setBotonesDeshabilitados(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/conductores/${user.user_id}/adjudicar-con-credito`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reserva_id: reservaSeleccionada.id,
          precio_maximo_adjudicacion: precioAdjudicacion
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al adjudicar servicio');
      }
      
      const data = await response.json();
      
      // Actualizar el crédito en el estado
      setCreditoConductor(data.credito_restante);
      
      // Cerrar modal
      setIsModalCreditoOpen(false);
      setReservaSeleccionada(null);
      
      toast({
        title: 'Servicio adjudicado',
        description: `Servicio adjudicado correctamente. Crédito restante: ${data.credito_restante.toFixed(2)} puntos`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Recargar la lista de reservas
      window.location.reload();
      
    } catch (error) {
      setBotonesDeshabilitados(false);
      setIsModalCreditoOpen(false);
      setReservaSeleccionada(null);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const renderCellValue = (reserva, key) => {
    const value = reserva[key];
    
    // Renderizar botón "Me interesa" con precio
    if (key === 'me_interesa') {
      // Si el conductor tiene suficiente crédito, mostrar botón de crédito
      if (creditoConductor >= precioAdjudicacion) {
        return (
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme="green"
              onClick={(e) => {
                e.stopPropagation();
                handleAdjudicarConCredito(reserva);
              }}
              isDisabled={botonesDeshabilitados}
            >
              ⭐ Quiero este servicio por {precioAdjudicacion || '1'} puntos
            </Button>
          </Box>
        );
      }
      
      // Si no tiene suficiente crédito, mostrar botones de pago
      return (
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            size="sm"
            colorScheme="purple"
            onClick={(e) => {
              e.stopPropagation();
              handleAdjudicarServicio(reserva.id, 'Bz');
            }}
            isDisabled={botonesDeshabilitados || isTestEnvironment}
            title={isTestEnvironment ? "Bizum no disponible en entorno de pruebas" : ""}
          >
            Bizum {precioAdjudicacion || '1'} €
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={(e) => {
              e.stopPropagation();
              handleAdjudicarServicio(reserva.id, 'T');
            }}
            isDisabled={botonesDeshabilitados}
          >
            Tarjeta {precioAdjudicacion || '1'} €
          </Button>
        </Box>
      );
    }

    // Renderizar idioma con icono de bandera
    if (key === 'idioma') {
      const idiomaLower = value ? value.toLowerCase() : 'es';
      const iconosDisponibles = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca'];
      const iconoPath = iconosDisponibles.includes(idiomaLower) 
        ? `/assets/icons/${idiomaLower}.png` 
        : '/assets/icons/es.png';
      
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <img 
            src={iconoPath} 
            alt={idiomaLower} 
            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
          />
        </Box>
      );
    }
    
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Renderizar estado con badge de color
    if (key === 'estado') {
      const color = getEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`estado_${value}`)}
        </Badge>
      );
    }

    // Renderizar sub_estado con badge de color
    if (key === 'sub_estado') {
      const color = getSubEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`subestado_${value}`)}
        </Badge>
      );
    }

    // Formatear fechas
    if (key.includes('fecha') || key.includes('created_at') || key.includes('updated_at')) {
      try {
        const date = new Date(value);
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return String(value);
      }
    }

    // Formatear números decimales
    if (typeof value === 'number') {
      return value.toFixed(2);
    }

    return String(value);
  };

  const formatColumnHeader = (key) => {
    if (key === 'me_interesa') return t('conductor_me_interesa');
    return t(`col_${key}`);
  };

  const handleGeolocationGranted = () => {
    console.log('[BolsaServicios] Geolocalización concedida');
    setGeolocationGranted(true);
  };

  const handleGeolocationDenied = () => {
    console.log('[BolsaServicios] Geolocalización denegada');
    setGeolocationGranted(false);
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="xl" />
        <Text ml={4}>{t('conductor_cargando_servicios')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" overflow="auto">
      <GeolocationCheck 
        onGeolocationGranted={handleGeolocationGranted}
        onGeolocationDenied={handleGeolocationDenied}
      />
      
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={2}
        style={{ 
          pointerEvents: geolocationGranted ? 'auto' : 'none',
          opacity: geolocationGranted ? 1 : 0.3
        }}
      >
        <Text fontSize="xl" fontWeight="bold">
          {t('conductor_titulo_bolsa_servicios')}
        </Text>
      </Box>
      
      <Box 
        mb={4}
        p={3}
        borderRadius="md"
        bgGradient="linear(to-r, green.400, teal.500)"
        style={{ 
          pointerEvents: geolocationGranted ? 'auto' : 'none',
          opacity: geolocationGranted ? 1 : 0.3
        }}
      >
        <Text fontSize="lg" fontWeight="bold" color="white" textAlign="center">
          Crédito disponible: {creditoConductor.toFixed(2)} puntos
        </Text>
      </Box>

      <Box
        style={{ 
          pointerEvents: geolocationGranted ? 'auto' : 'none',
          opacity: geolocationGranted ? 1 : 0.3
        }}
      >
      {reservas.length > 0 ? (
        <Table variant="simple" width="100%" minWidth="1200px">
          <Thead>
            <Tr>
              {columnsToDisplay.map((key) => (
                <Th
                  key={key}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                >
                  {formatColumnHeader(key)}
                  {sortBy === key && (sortOrder === 'asc' ? ' ⬆️' : ' ⬇️')}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {reservas.map((reserva) => (
              <Tr
                key={reserva.id}
                _hover={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', cursor: 'pointer' }}
              >
                {columnsToDisplay.map((key) => (
                  <Td key={`${reserva.id}-${key}`}>
                    {renderCellValue(reserva, key)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text>{t('conductor_sin_servicios_bolsa')}</Text>
      )}
      </Box>

      {/* Modal de confirmación de adjudicación con crédito */}
      <Modal 
        isOpen={isModalCreditoOpen} 
        onClose={() => {
          setIsModalCreditoOpen(false);
          setReservaSeleccionada(null);
        }} 
        isCentered 
        size="lg"
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <ModalContent>
          <ModalHeader textAlign="center" fontSize="xl" color="blue.600" pb={2}>
            ⭐ Confirmación de Adjudicación
          </ModalHeader>
          <ModalBody pb={4}>
            <VStack spacing={4} align="stretch">
              <Alert status="info" variant="left-accent" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold" fontSize="md" mb={2}>
                    Vas a adjudicarte este servicio por {precioAdjudicacion} puntos
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    Al confirmar, se descontarán <strong>{precioAdjudicacion} puntos</strong> de tu crédito disponible 
                    y quedarás obligado a realizar este servicio en virtud de los Reglamentos aplicables.
                  </Text>
                </Box>
              </Alert>

              {reservaSeleccionada && (
                <Box bg="gray.50" p={3} borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}><strong>Origen:</strong> {reservaSeleccionada.origen_direccion}</Text>
                  <Text fontSize="sm" color="gray.600" mb={1}><strong>Destino:</strong> {reservaSeleccionada.destino_direccion}</Text>
                  <Text fontSize="sm" color="gray.600"><strong>Fecha:</strong> {new Date(reservaSeleccionada.fecha_hora_inicio).toLocaleString('es-ES')}</Text>
                </Box>
              )}

              <Box bg="blue.50" p={3} borderRadius="md">
                <Text fontSize="sm" color="blue.800">
                  <strong>Crédito actual:</strong> {creditoConductor.toFixed(2)} puntos
                </Text>
                <Text fontSize="sm" color="blue.800">
                  <strong>Crédito después:</strong> {(creditoConductor - precioAdjudicacion).toFixed(2)} puntos
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter justifyContent="center" gap={3} pt={2}>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsModalCreditoOpen(false);
                setReservaSeleccionada(null);
              }}
              isDisabled={botonesDeshabilitados}
            >
              Cancelar
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={confirmarAdjudicacionCredito}
              isDisabled={botonesDeshabilitados}
              isLoading={botonesDeshabilitados}
            >
              Continuar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BolsaServicios;
